import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import BidOverviewColumns from "./BidOverviewDataTable";
import { useAppSelector } from "../redux/hooks/redux-hooks";
import { DashboardDetailsModel } from "../models/DashboardDetailsModel";
import { appConfig, projectBidStatusObj } from "../utility/config";
import StackedColumnChart from "./charts/StackColumnChart";
import { CircularProgress, Grid } from "@mui/material";
import StackedBarChart from "./charts/StackBarChart";
import WinLossTable from "./project-list/win-loss-table";
import SbuBidsTable from "./project-list/sbu-bids-table";
import StackedWaterfallChart from "./charts/StackedWaterfallChart";

type FieldValueType = string | number | boolean | Date | null;

type Props = {
  dashboardFilteredResponse: DashboardDetailsModel[];
  fromDate: Date;
  endDate: Date;
};

const WinLoss = ({ dashboardFilteredResponse, fromDate, endDate }: Props) => {
  const [stackColumnData, setStackcolumData] = useState<any>({});
  const [stackHorizontalData, setStackHorizontalData] = useState<any>({});
  const [sbuWinLossData, setSbuWinLossData] = useState<any>({});
  const [tableData, setTableData] = useState<any>({});
  const [totalCount, setTotalCount] = useState<number>(0);
  const [topWonBids, setTopWonBids] = useState<any>();
  const [topLossBids, setTopLossBids] = useState<any>();
  const [maxBidderDetailCount, setMaxBidderDetailCount] = useState(0);

  const isDashboardDetailsFetching = useAppSelector(
    (state) => state.dashboardDetails.isFetching
  );

  const bidderDetailsObj = {
    rank: "rank_",
    bidderName: "bidderName_",
    evaluatedPrice: "evaluatedPrice_",
    remarks: "remarks_",
  };

  const hideColumns: GridColumnVisibilityModel = {
    contractType: false,
    state: false,
    country: false,
    bidSubmissionDate: false,
    goGetFactor: false,
    workStarted: false,
    bidManagerName: false,
  };

  const filterEndDate = useMemo(() => {
    return new Date(endDate);
  }, [endDate]);

  const fyStartDate = useMemo(() => {
    let fyDate: Date;
    const endDateMonth = filterEndDate.getMonth() + 1;
    const endDateYear = filterEndDate.getFullYear();

    if (endDateMonth === 1 || endDateMonth === 2 || endDateMonth === 3) {
      fyDate = new Date(`${endDateYear - 1}-04-01`);
    } else {
      fyDate = new Date(`${endDateYear}-04-01`);
    }
    return fyDate;
  }, [filterEndDate]);

  dashboardFilteredResponse.forEach((item) => {
    let count: number = item.projectBidderDetails
      ? item.projectBidderDetails.length
      : 0;
    if (count > maxBidderDetailCount) setMaxBidderDetailCount(count);
  });

  const columns = useMemo(() => {
    const projectColumns: GridColDef[] = [...BidOverviewColumns];
    projectColumns.forEach((each) => {
      if (each.headerName === "Go-NoGo Status" || each.headerName === "Win/Loss Status") {
        each.headerName = "Win/Loss Status";
      }
    });
    // To get column names for bidder details
    function getColumnsProps(i: number) {
      return [
        // {
        //   field: `${bidderDetailsObj.rank}${i}`,
        //   headerName: `Rank ${i}`,
        //   type: "string",
        //   width: 100,
        //   valueGetter: ({ value }: any) => {
        //     return value || "";
        //   },
        // },
        {
          field: `${bidderDetailsObj.bidderName}${i}`,
          headerName: `Bidder Name ${i}`,
          type: "string",
          width: 150,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
        {
          field: `${bidderDetailsObj.evaluatedPrice}${i}`,
          headerName: `Evaluated Price ${i}`,
          type: "string",
          width: 150,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
        {
          field: `${bidderDetailsObj.remarks}${i}`,
          headerName: `Remarks ${i}`,
          type: "string",
          width: 150,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
      ];
    }

    if (maxBidderDetailCount !== 0) {
      for (let i = 1; i <= maxBidderDetailCount; i++) {
        const bidderDetailFieldProps = getColumnsProps(i);
        projectColumns.push(...bidderDetailFieldProps);
      }
    }

    return projectColumns;
  }, [
    maxBidderDetailCount,
    // bidderDetailsObj.rank,
    bidderDetailsObj.bidderName,
    bidderDetailsObj.evaluatedPrice,
    bidderDetailsObj.remarks,
  ]);

  const rows = useMemo(() => {
    const projectRows: any[] = [];
    dashboardFilteredResponse.forEach((item) => {
      const projectStatus = item?.projectStatus.toLowerCase();
      const bidSubmittedStatusDate = new Date(item.bidSubmittedStatusDate);
      const winDate = item.winDate && new Date(item.winDate);
      const lossDate = item.lossDate && new Date(item.lossDate);
      const cancelledDate = item.cancelledDate && new Date(item.cancelledDate);

      if (
        ((projectStatus === projectBidStatusObj.win ||
          projectStatus === projectBidStatusObj.loss ||
          projectStatus === projectBidStatusObj.cancelled) &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.win &&
          winDate &&
          fyStartDate <= winDate &&
          winDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.loss &&
          lossDate &&
          fyStartDate <= lossDate &&
          lossDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.cancelled &&
          cancelledDate &&
          fyStartDate <= cancelledDate &&
          cancelledDate <= filterEndDate)
      ) {
        const projectObj: { [key: string]: FieldValueType } = {};
        Object.entries(item).forEach(([key, value]) => {
          projectObj[key] = value;
        });

        if (item.projectBidderDetails) {
          item.projectBidderDetails.forEach((bidderDetails, i) => {
            const index = i + 1;
            projectObj[`${bidderDetailsObj.rank}${index}`] = bidderDetails.rank;
            projectObj[`${bidderDetailsObj.bidderName}${index}`] =
              bidderDetails.bidderName;
            projectObj[`${bidderDetailsObj.evaluatedPrice}${index}`] =
              bidderDetails.evaluatedPrice;
            projectObj[`${bidderDetailsObj.remarks}${index}`] =
              bidderDetails.remarks;
          });
        }
        projectRows.push(projectObj);
      }
    });
    return projectRows;
  }, [
    dashboardFilteredResponse,
    fyStartDate,
    filterEndDate,
    bidderDetailsObj.rank,
    bidderDetailsObj.bidderName,
    bidderDetailsObj.evaluatedPrice,
    bidderDetailsObj.remarks,
  ]);

  const all_sbus = useAppSelector((state) => state.sbu.all_SBUs);

  useEffect(() => {
    const oldUnderEvaluationCounts: any = {};
    const bidsubmitted: any = {};
    const win: any = {};
    const loss: any = {};
    const cancelled: any = {};
    const newUnderEvaluationCounts: any = {};

    if (all_sbus && all_sbus.length !== 0) {
      all_sbus.forEach((each) => {
        oldUnderEvaluationCounts[each.name] = {
          count: 0,
          value: 0,
        };
        bidsubmitted[each.name] = {
          count: 0,
          value: 0,
        };
        win[each.name] = {
          count: 0,
          value: 0,
        };
        loss[each.name] = {
          count: 0,
          value: 0,
        };
        cancelled[each.name] = {
          count: 0,
          value: 0,
        };
        newUnderEvaluationCounts[each.name] = {
          count: 0,
          value: 0,
        };
      });
      dashboardFilteredResponse.forEach((each) => {
        const projectStatus = each?.projectStatus.toLowerCase();
        const bidSubmittedStatusDate = new Date(each.bidSubmittedStatusDate);
        const winDate = each.winDate && new Date(each.winDate);
        const lossDate = each.lossDate && new Date(each.lossDate);
        const cancelledDate =
          each.cancelledDate && new Date(each.cancelledDate);

        if (
          bidSubmittedStatusDate < fyStartDate &&
          ((projectStatus === projectBidStatusObj.win &&
            winDate &&
            winDate >= fyStartDate) ||
            (projectStatus === projectBidStatusObj.loss &&
              lossDate &&
              lossDate >= fyStartDate) ||
            (projectStatus === projectBidStatusObj.cancelled &&
              cancelledDate &&
              cancelledDate >= fyStartDate) ||
            projectStatus === projectBidStatusObj.bidSubmitted)
        ) {
          oldUnderEvaluationCounts[each?.sbu].count++;
          oldUnderEvaluationCounts[each?.sbu].value += each.estimatedValue;
        }
        if (
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate &&
          (projectStatus === projectBidStatusObj.bidSubmitted ||
            projectStatus === projectBidStatusObj.win ||
            projectStatus === projectBidStatusObj.loss)
        ) {
          bidsubmitted[each?.sbu].count++;
          bidsubmitted[each?.sbu].value += each.estimatedValue;
        }
        if (
          (fyStartDate <= bidSubmittedStatusDate &&
            bidSubmittedStatusDate <= filterEndDate &&
            projectStatus === projectBidStatusObj.win) ||
          (bidSubmittedStatusDate < fyStartDate &&
            projectStatus === projectBidStatusObj.win &&
            winDate &&
            fyStartDate <= winDate &&
            winDate <= filterEndDate)
        ) {
          win[each.sbu].count++;
          win[each.sbu].value += each.estimatedValue;
        }
        if (
          (fyStartDate <= bidSubmittedStatusDate &&
            bidSubmittedStatusDate <= filterEndDate &&
            projectStatus === projectBidStatusObj.loss) ||
          (bidSubmittedStatusDate < fyStartDate &&
            projectStatus === projectBidStatusObj.loss &&
            lossDate &&
            fyStartDate <= lossDate &&
            lossDate <= filterEndDate)
        ) {
          loss[each?.sbu].count++;
          loss[each?.sbu].value += each.estimatedValue;
        }
        if (
          (bidSubmittedStatusDate < fyStartDate &&
            projectStatus === projectBidStatusObj.cancelled &&
            cancelledDate &&
            fyStartDate <= cancelledDate &&
            cancelledDate <= filterEndDate) ||
          (fyStartDate <= bidSubmittedStatusDate &&
            bidSubmittedStatusDate <= filterEndDate &&
            projectStatus === projectBidStatusObj.cancelled)
        ) {
          cancelled[each.sbu].count++;
          cancelled[each.sbu].value += each.estimatedValue;
        }
        if (each.sbu) {
          newUnderEvaluationCounts[each?.sbu].count =
            oldUnderEvaluationCounts[each?.sbu].count +
            bidsubmitted[each?.sbu].count -
            win[each?.sbu].count -
            loss[each?.sbu].count -
            cancelled[each?.sbu].count;
          newUnderEvaluationCounts[each?.sbu].value =
            oldUnderEvaluationCounts[each?.sbu].value +
            bidsubmitted[each?.sbu].value -
            win[each?.sbu].value -
            loss[each?.sbu].value -
            cancelled[each?.sbu].value;
        }
      });
      setStackcolumData({
        "LastFY Under Evaluation": oldUnderEvaluationCounts,
        "Bid Submitted": bidsubmitted,
        Won: win,
        Lost: loss,
        Cancelled: cancelled,
        "Under Evaluation": newUnderEvaluationCounts,
      });
    }
  }, [dashboardFilteredResponse, all_sbus, filterEndDate, fyStartDate]);

  const StackedColumChartData = useMemo(() => {
    const data: any = [];

    let sumOfPrev = 0;
    Object.entries(stackColumnData).forEach((value: any, index: number) => {
      const subData: any = {};
      if (index === 0) {
        let j = 1;
        subData["category"] = value[0];
        for (let d in value[1]) {
          subData[`open${j}`] =
            j - 1 > 0 ? subData[`close${j - 1}`] : sumOfPrev;
          subData[`close${j}`] = subData[`open${j}`] + value[1][d].value;
          sumOfPrev += value[1][d].value;
          subData[`count${j}`] = value[1][d].count;
          j++;
        }
      }
      if (index > 0) {
        let i = 1;
        let sumOfCurrent = 0;
        subData["category"] = value[0];
        for (let v in value[1]) {
          sumOfCurrent += value[1][v].value;
        }
        if (index > 1) {
          sumOfPrev = sumOfPrev - sumOfCurrent;
        }
        for (let d in value[1]) {
          subData[`open${i}`] =
            i - 1 > 0 ? subData[`close${i - 1}`] : sumOfPrev;
          subData[`close${i}`] = subData[`open${i}`] + value[1][d].value;
          sumOfPrev += value[1][d].value;
          subData[`count${i}`] = value[1][d].count;
          i++;
        }
        if (index > 1) {
          sumOfPrev = sumOfPrev - sumOfCurrent;
        }
      }
      data.push(subData);
    });
    return data;
  }, [stackColumnData]);

  useEffect(() => {
    const win: any = { count: 0, value: 0 };
    const loss: any = { count: 0, value: 0 };
    dashboardFilteredResponse.forEach((each) => {
      const projectStatus = each.projectStatus.toLowerCase();
      const bidSubmittedStatusDate = new Date(each.bidSubmittedStatusDate);
      const winDate = each.winDate && new Date(each.winDate);
      const lossDate = each.lossDate && new Date(each.lossDate);

      if (
        (projectStatus === projectBidStatusObj.win &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.win &&
          winDate &&
          fyStartDate <= winDate &&
          winDate <= filterEndDate)
      ) {
        win.count++;
        win.value += each.estimatedValue;
      }
      if (
        (projectStatus === projectBidStatusObj.loss &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.loss &&
          lossDate &&
          fyStartDate <= lossDate &&
          lossDate <= filterEndDate)
      ) {
        loss.count++;
        loss.value += each.estimatedValue;
      }
    });
    setStackHorizontalData({
      Won: win,
      Lost: loss,
    });
  }, [dashboardFilteredResponse, filterEndDate, fyStartDate]);

  const StackedHorizontalChartData = useMemo(() => {
    const data: any = [];
    const subData: any = {};
    subData["category"] = "TPL";
    for (const key in stackHorizontalData) {
      subData[key] = stackHorizontalData[key].value;
      subData[`${key}Count`] = stackHorizontalData[key].count;
    }
    data.push(subData);
    return data;
  }, [stackHorizontalData]);

  useEffect(() => {
    let counts: any = {};
    if (all_sbus && all_sbus.length > 0) {
      all_sbus.forEach((each) => {
        counts[each.name] = {
          won: { count: 0, value: 0 },
          lost: { count: 0, value: 0 },
        };
      });
    }

    dashboardFilteredResponse.forEach((each) => {
      const projectStatus = each.projectStatus.toLowerCase();
      const bidSubmittedStatusDate = new Date(each.bidSubmittedStatusDate);
      const winDate = each.winDate && new Date(each.winDate);
      const lossDate = each.lossDate && new Date(each.lossDate);

      if (
        (projectStatus === projectBidStatusObj.win &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.win &&
          winDate &&
          fyStartDate <= winDate &&
          winDate <= filterEndDate)
      ) {
        counts[each.sbu].won.count++;
        counts[each.sbu].won.value += each.estimatedValue;
      }
      if (
        (projectStatus === projectBidStatusObj.loss &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.loss &&
          lossDate &&
          fyStartDate <= lossDate &&
          lossDate <= filterEndDate)
      ) {
        counts[each.sbu].lost.count++;
        counts[each.sbu].lost.value += each.estimatedValue;
      }
    });
    setSbuWinLossData(counts);
  }, [dashboardFilteredResponse, filterEndDate, fyStartDate, all_sbus]);

  const sbuHorizontalChartData = useMemo(() => {
    const data: any = [];
    for (let each in sbuWinLossData) {
      const subData: any = {};
      subData["category"] = each;
      for (let d in sbuWinLossData[each]) {
        subData[d] = sbuWinLossData[each][d].value;
        subData[`${d}Count`] = sbuWinLossData[each][d].count;
      }
      data.push(subData);
    }
    return data;
  }, [sbuWinLossData]);

  useEffect(() => {
    let totalCounts: any = {
      "Won": 0,
      "Lost":0,
      "Cancelled":0,
      "Under Evaluation":0,
      "Bid Submitted":0,
      "LastFY Under Evaluation":0
    };
    setTotalCount(0);

    for (const category in stackColumnData) {
      if (stackColumnData.hasOwnProperty(category)) {
        for (const subCategory in stackColumnData[category]) {
          if (stackColumnData[category].hasOwnProperty(subCategory)) {
            totalCounts[category] += parseInt(stackColumnData[category][subCategory].value);
          }
        }
      }
    }
    setTotalCount(totalCounts["Won"] + totalCounts["Lost"] + totalCounts["Cancelled"] + totalCounts["Under Evaluation"]);

    setTableData([
      { name: "Won", value: totalCounts["Won"] },
      { name: "Lost", value: totalCounts["Lost"] },
      { name: "Cancelled", value: totalCounts["Cancelled"] },
      { name: "Under Evaluation", value: totalCounts["Under Evaluation"] },
      { name: "Total", value: totalCounts["Won"] + totalCounts["Lost"] + totalCounts["Cancelled"] + totalCounts["Under Evaluation"] },
    ]);
  }, [dashboardFilteredResponse, stackColumnData, filterEndDate, fyStartDate]);

  useEffect(() => {
    const winArray: {
      projectID: number;
      projectName: string;
      sbu: string;
      value: number;
    }[] = [];
    const lossArray: {
      projectID: number;
      projectName: string;
      sbu: string;
      value: number;
    }[] = [];
    dashboardFilteredResponse.forEach((each) => {
      const projectStatus = each.projectStatus.toLowerCase();
      const bidSubmittedStatusDate = new Date(each.bidSubmittedStatusDate);
      const winDate = each.winDate && new Date(each.winDate);
      const lossDate = each.lossDate && new Date(each.lossDate);

      if (
        (projectStatus === projectBidStatusObj.win &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.win &&
          winDate &&
          fyStartDate <= winDate &&
          winDate <= filterEndDate)
      ) {
        winArray.push({
          projectID: each.projectID,
          projectName: each.projectName,
          sbu: each.sbu,
          value: each.estimatedValue,
        });
      }
      if (
        (projectStatus === projectBidStatusObj.loss &&
          fyStartDate <= bidSubmittedStatusDate &&
          bidSubmittedStatusDate <= filterEndDate) ||
        (bidSubmittedStatusDate < fyStartDate &&
          projectStatus === projectBidStatusObj.loss &&
          lossDate &&
          fyStartDate <= lossDate &&
          lossDate <= filterEndDate)
      ) {
        lossArray.push({
          projectID: each.projectID,
          projectName: each.projectName,
          sbu: each.sbu,
          value: each.estimatedValue,
        });
      }
    });
    let sortedWinData: any;
    if (winArray.length > 0) {
      sortedWinData = winArray
        .map((project) => ({ ...project }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 2);
    } else {
      sortedWinData = "No Bids";
    }

    let sortedLossData: any;
    if (lossArray.length > 0) {
      sortedLossData = lossArray
        .map((project) => ({ ...project }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 2);
    } else {
      sortedLossData = "No Bids";
    }
    if (sortedWinData) {
      setTopWonBids(sortedWinData);
    }
    if (sortedLossData) {
      setTopLossBids(sortedLossData);
    }
  }, [dashboardFilteredResponse, filterEndDate, fyStartDate]);

  return (
    <>
      <Grid
        container
        height={`calc(100vh - ${
          appConfig.headerHeight + appConfig.dashboardStackHeight
        }px) `}
        sx={{ overflowY: "auto" }}
      >
        <Grid
          item
          container
          xs={12}
          md={8}
          display="flex"
          flexDirection="column"
        >
          {isDashboardDetailsFetching ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress color="primary" className="loader-spinner" />
            </div>
          ) : (
            <>
              <Grid item className="chart-container">
                <StackedWaterfallChart
                  data={StackedColumChartData}
                  allSbus={all_sbus}
                />
              </Grid>
              <Grid item style={{ height: "20vh" }}>
                <StackedBarChart data={StackedHorizontalChartData} />
              </Grid>
              <Grid item className="chart-container">
                <StackedColumnChart data={sbuHorizontalChartData} />
              </Grid>
            </>
          )}
        </Grid>
        <Grid item container md={4} display="flex" flexDirection="column">
          <Grid item sx={{ p: 1 }}>
            <WinLossTable data={tableData} totalCount={totalCount} />
          </Grid>
          <Grid item sx={{ p: 1 }}>
            <SbuBidsTable heading="Won Bids" data={topWonBids} />
          </Grid>
          <Grid item sx={{ p: 1 }}>
            <SbuBidsTable heading="Lost Bids" data={topLossBids} />
          </Grid>
        </Grid>

        <DataGrid
          columns={columns}
          rows={rows}
          columnVisibilityModel={hideColumns}
          getRowId={(row) => row.projectID}
          style={{ height: 350, width: "100%" }}
          sx={{
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: "#348fea",
            },
          }}
        />
      </Grid>
    </>
  );
};

export default WinLoss;
